// Inspection checklist component — categorized photo capture guide for pickup/return

import { useState } from 'react';

interface ChecklistItem {
  key: string;
  group: string;
  label: string;
  required: boolean;
  photoId?: string;
  photoUrl?: string;
}

const CHECKLIST: ChecklistItem[] = [
  // Exterior
  { key: 'exterior_front',   group: '외관', label: '전면',   required: true },
  { key: 'exterior_rear',    group: '외관', label: '후면',   required: true },
  { key: 'exterior_left',    group: '외관', label: '좌측',   required: true },
  { key: 'exterior_right',   group: '외관', label: '우측',   required: true },
  { key: 'exterior_top',     group: '외관', label: '탑',     required: false },
  // Interior
  { key: 'interior_dash',    group: '실내', label: '대시보드', required: true },
  { key: 'interior_seats',   group: '실내', label: '시트',    required: true },
  { key: 'interior_trunk',   group: '실내', label: '트렁크',  required: false },
  // Details
  { key: 'detail_wheels',    group: '상세', label: '타이어/휠', required: true },
  { key: 'detail_glass',     group: '상세', label: '유리/미러', required: false },
  { key: 'detail_engine',    group: '상세', label: '엔진룸',    required: false },
  { key: 'detail_odometer',  group: '상세', label: '계기판(주행거리)', required: true },
  { key: 'detail_damage',    group: '상세', label: '손상 부위', required: false },
];

interface Props {
  mode: 'pickup' | 'return';
  photos: Record<string, { id: string; url: string }>; // key → photo mapping
  pickupPhotos?: Record<string, { id: string; url: string }>; // for return comparison
  onCapture: (itemKey: string) => void;
}

export default function InspectionChecklist({ mode, photos, pickupPhotos, onCapture }: Props) {
  const [expandedGroup, setExpandedGroup] = useState<string | null>('외관');

  const groups = CHECKLIST.reduce((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {} as Record<string, ChecklistItem[]>);

  const totalRequired = CHECKLIST.filter(i => i.required).length;
  const capturedRequired = CHECKLIST.filter(i => i.required && photos[i.key]).length;
  const progress = Math.round((capturedRequired / totalRequired) * 100);

  const getCardColor = (key: string, url?: string) => {
    if (url) return 'border-emerald-400 bg-emerald-50';
    if (CHECKLIST.find(i => i.key === key)?.required) return 'border-amber-300 bg-amber-50';
    return 'border-gray-200 bg-white';
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      {/* Header with progress */}
      <div className="p-5 border-b border-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">
            {mode === 'pickup' ? '🚗 인수 시 검수 체크리스트' : '🔙 반납 시 검수 체크리스트'}
          </h3>
          <span className={`text-sm font-medium ${progress === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
            {capturedRequired}/{totalRequired} 필수
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${progress === 100 ? 'bg-emerald-500' : 'bg-amber-400'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Checklist groups */}
      <div className="divide-y divide-gray-50">
        {Object.entries(groups).map(([groupName, items]) => (
          <div key={groupName}>
            {/* Group header */}
            <button
              onClick={() => setExpandedGroup(expandedGroup === groupName ? null : groupName)}
              className="w-full flex items-center justify-between px-5 py-3 bg-gray-50/50 hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <span className="text-sm font-semibold text-slate-700">{groupName}</span>
              <span className="text-xs text-gray-400">
                {items.filter(i => photos[i.key]).length}/{items.length}
              </span>
            </button>

            {/* Group items */}
            {expandedGroup === groupName && (
              <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {items.map(item => {
                  const photo = photos[item.key];
                  const pickupPhoto = pickupPhotos?.[item.key];

                  return (
                    <div key={item.key}>
                      {/* Photo card */}
                      <button
                        onClick={() => onCapture(item.key)}
                        className={`w-full aspect-[4/3] rounded-xl border-2 overflow-hidden relative transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${getCardColor(item.key, photo?.url)}`}
                      >
                        {photo?.url ? (
                          <img src={photo.url} alt={item.label} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                            <svg className="w-6 h-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            </svg>
                            <span className="text-xs">촬영</span>
                          </div>
                        )}
                        {/* Required badge */}
                        {item.required && !photo?.url && (
                          <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-md">필수</span>
                        )}
                        {/* Captured check */}
                        {photo?.url && (
                          <span className="absolute top-1 left-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        )}
                      </button>
                      {/* Label */}
                      <p className="text-xs text-center mt-1 text-gray-500">{item.label}</p>
                      {/* Comparison (pickup vs return) */}
                      {mode === 'return' && pickupPhoto?.url && (
                        <div className="mt-1">
                          <p className="text-[10px] text-gray-400 text-center">인수 시</p>
                          <div className="w-full aspect-[4/3] rounded-lg overflow-hidden border border-gray-200 mt-0.5">
                            <img src={pickupPhoto.url} alt={`인수 ${item.label}`} className="w-full h-full object-cover opacity-60" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Guidance */}
      <div className="p-5 bg-gray-50 rounded-b-2xl">
        <p className="text-xs text-gray-500">
          {mode === 'pickup'
            ? '📸 차량 인수 시 모든 부위를 빠짐없이 촬영해주세요. 필수 항목은 반드시 촬영해야 합니다.'
            : '📸 반납 시 동일한 부위를 촬영하여 인수 시 사진과 비교합니다. 새로운 손상이 있으면 손상 부위를 클로즈업해주세요.'}
        </p>
      </div>
    </div>
  );
}
